import { Pipe, PipeTransform } from '@angular/core';

type MaxWordsMode = 'maxWords' | 'maxChars';

/**
 * Truncates a string and appends an ellipsis.
 *
 * - `maxWords`: keeps at most `limit` words, then appends `...`.
 * - `maxChars`: keeps whole words until the `limit` characters are reached,
 *   stopping at the end of the word that crosses the limit, then appends `...`.
 *
 * Usage:
 *   {{ text | maxWords:'maxWords':20 }}
 *   {{ text | maxWords:'maxChars':100 }}
 */
@Pipe({ name: 'maxWords' })
export class MaxWordsPipe implements PipeTransform {
  transform(value: string | null | undefined, mode: MaxWordsMode, limit: number): string {
    if (!value) {
      return '';
    }

    if (limit <= 0) {
      return '';
    }

    const trimmed = value.trim();
    const words = trimmed.split(/\s+/);

    if (mode === 'maxWords') {
      if (words.length <= limit) {
        return trimmed;
      }
      return words.slice(0, limit).join(' ') + '...';
    }

    // maxChars: build up whole words until the character limit is reached.
    if (trimmed.length <= limit) {
      return trimmed;
    }

    let result = '';
    for (const word of words) {
      result = result ? `${result} ${word}` : word;
      if (result.length >= limit) {
        break;
      }
    }

    if (result.length >= trimmed.length) {
      return trimmed;
    }

    return result + '...';
  }
}
